import { uuid } from "@/utils/uuid"
import { PixiesetAccountInfoStatus } from "@/api/dto/users"

export const PIXIESET_UPGRADE_RETURN_PARAM = "pixieset_upgrade_return"
export const PIXIESET_UPGRADE_RETURN_TO_KEY = "pixieset-upgrade-return-to"
export const PIXIESET_UPGRADE_COMPLETE_KEY = "pixieset-upgrade-complete"
export const PIXIESET_UPGRADE_ACK_KEY = "pixieset-upgrade-ack"
export const PIXIESET_UPGRADE_TAB_ID_KEY = "pixieset-upgrade-tab-id"
export const PIXIESET_SUBSCRIPTION_SNAPSHOT_KEY_PREFIX = "pixieset-subscription-snapshot"
export const PIXIESET_SUBSCRIPTION_SNAPSHOT_TTL_MS = 10 * 60 * 1000

export interface PixiesetSubscriptionSnapshot {
	tabId: string
	timestamp: number
	accountInfoStatus: PixiesetAccountInfoStatus
	billingPlan: string | null
}

const pixiesetAccountInfoStatusByValue = new Map<number, PixiesetAccountInfoStatus>([
	[PixiesetAccountInfoStatus.Free, PixiesetAccountInfoStatus.Free],
	[PixiesetAccountInfoStatus.Upgraded, PixiesetAccountInfoStatus.Upgraded],
	[PixiesetAccountInfoStatus.Expired, PixiesetAccountInfoStatus.Expired],
	[PixiesetAccountInfoStatus.Deleted, PixiesetAccountInfoStatus.Deleted],
	[PixiesetAccountInfoStatus.Delinquent, PixiesetAccountInfoStatus.Delinquent],
])

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null && !Array.isArray(value)

const parsePixiesetSubscriptionSnapshot = (
	rawValue: string,
	expectedTabId: string,
): PixiesetSubscriptionSnapshot | null => {
	const parsed: unknown = JSON.parse(rawValue)
	if (!isRecord(parsed)) {
		return null
	}

	const { tabId, timestamp, accountInfoStatus, billingPlan } = parsed
	const normalizedAccountInfoStatus =
		typeof accountInfoStatus === "number" && Number.isFinite(accountInfoStatus)
			? pixiesetAccountInfoStatusByValue.get(accountInfoStatus)
			: undefined

	if (
		typeof tabId !== "string" ||
		tabId !== expectedTabId ||
		typeof timestamp !== "number" ||
		!Number.isFinite(timestamp) ||
		normalizedAccountInfoStatus == null
	) {
		return null
	}

	return {
		tabId,
		timestamp,
		accountInfoStatus: normalizedAccountInfoStatus,
		billingPlan: typeof billingPlan === "string" && billingPlan.length > 0 ? billingPlan : null,
	}
}

const getLocalStorage = (): Storage | null => {
	if (typeof window === "undefined") {
		return null
	}

	try {
		return window.localStorage
	} catch (error) {
		console.warn("Pixieset upgrade localStorage unavailable.", error)
		return null
	}
}

export const getUpgradeLocalStorageItem = (key: string): string | null => {
	const storage = getLocalStorage()
	if (!storage) {
		return null
	}

	try {
		return storage.getItem(key)
	} catch (error) {
		console.warn(`Pixieset upgrade localStorage get failed for ${key}.`, error)
		return null
	}
}

export const setUpgradeLocalStorageItem = (key: string, value: string): boolean => {
	const storage = getLocalStorage()
	if (!storage) {
		return false
	}

	try {
		storage.setItem(key, value)
		return true
	} catch (error) {
		console.warn(`Pixieset upgrade localStorage set failed for ${key}.`, error)
		return false
	}
}

export const removeUpgradeLocalStorageItem = (key: string): boolean => {
	const storage = getLocalStorage()
	if (!storage) {
		return false
	}

	try {
		storage.removeItem(key)
		return true
	} catch (error) {
		console.warn(`Pixieset upgrade localStorage remove failed for ${key}.`, error)
		return false
	}
}

export const getPixiesetSubscriptionSnapshotKey = (tabId: string): string =>
	`${PIXIESET_SUBSCRIPTION_SNAPSHOT_KEY_PREFIX}:${tabId}`

export const getPixiesetSubscriptionSnapshot = (
	tabId: string,
): PixiesetSubscriptionSnapshot | null => {
	const key = getPixiesetSubscriptionSnapshotKey(tabId)
	const rawValue = getUpgradeLocalStorageItem(key)
	if (!rawValue) {
		return null
	}

	try {
		const parsed = parsePixiesetSubscriptionSnapshot(rawValue, tabId)
		if (!parsed) {
			removeUpgradeLocalStorageItem(key)
			return null
		}

		return parsed
	} catch {
		removeUpgradeLocalStorageItem(key)
		return null
	}
}

export const consumePixiesetSubscriptionSnapshot = (
	tabId: string,
): PixiesetSubscriptionSnapshot | null => {
	const key = getPixiesetSubscriptionSnapshotKey(tabId)
	const rawValue = getUpgradeLocalStorageItem(key)
	if (!rawValue) {
		return null
	}

	try {
		const parsed = parsePixiesetSubscriptionSnapshot(rawValue, tabId)
		removeUpgradeLocalStorageItem(key)
		if (!parsed) {
			return null
		}

		return parsed
	} catch {
		removeUpgradeLocalStorageItem(key)
		return null
	}
}

export const setPixiesetSubscriptionSnapshot = (
	tabId: string,
	snapshot: PixiesetSubscriptionSnapshot,
): boolean =>
	setUpgradeLocalStorageItem(getPixiesetSubscriptionSnapshotKey(tabId), JSON.stringify(snapshot))

export const removePixiesetSubscriptionSnapshot = (tabId: string): boolean =>
	removeUpgradeLocalStorageItem(getPixiesetSubscriptionSnapshotKey(tabId))

let fallbackTabId: string | null = null

export const getPixiesetUpgradeTabId = (): string => {
	if (typeof window === "undefined") {
		return "server"
	}

	try {
		const existing = window.sessionStorage.getItem(PIXIESET_UPGRADE_TAB_ID_KEY)
		if (existing) {
			return existing
		}

		const generated = uuid()
		window.sessionStorage.setItem(PIXIESET_UPGRADE_TAB_ID_KEY, generated)
		return generated
	} catch {
		if (!fallbackTabId) {
			fallbackTabId = uuid()
		}
		return fallbackTabId
	}
}
